import type { ApprovalResponse } from "src/components/useGuardrailChainActions";

export const INTERSTITIAL_HOLD_MS = 1000;
export const LAST_ITEM_WAIT_CAP_MS = 5000;

export type DecisionOutcome = ApprovalResponse;

export type InterstitialCopy = {
  title: string;
  description: string;
  line2: string;
  action: string;
  announce: string;
};

/** The key below the current one, else the key above, else null. */
export const nextKeyAfter = (
  keys: readonly string[],
  key: string
): string | null => {
  const index = keys.indexOf(key);
  if (index < 0) {
    return null;
  }
  return keys[index + 1] ?? keys[index - 1] ?? null;
};

const outcomeWord = (outcome: DecisionOutcome): string => {
  switch (outcome) {
    case "APPROVE":
      return "Approved";
    case "REFUSE":
      return "Refused";
    default: {
      const unexpected: never = outcome;
      return unexpected;
    }
  }
};

const joined = (displayId: string, title: string, separator: string): string =>
  [displayId, title].filter((part) => part !== "").join(separator);

export const interstitialCopy = (
  outcome: DecisionOutcome,
  displayId: string,
  title: string,
  left: number,
  nextTitle: string | null
): InterstitialCopy => {
  const word = outcomeWord(outcome);
  const description = joined(displayId, title, " · ");
  const subject = joined(displayId, title, " ");
  if (left === 0) {
    return {
      title: word,
      description,
      line2: "That was the last one",
      action: "Done",
      announce: `${word} ${subject}. You're all caught up.`,
    };
  }
  return {
    title: word,
    description,
    line2: `${left} left · Up next: ${nextTitle ?? ""}`,
    action: "Next item",
    announce: `${word} ${subject}. ${left} left. Opening ${nextTitle ?? ""}.`,
  };
};

export const interstitialAppearance = (
  outcome: DecisionOutcome
): { tone: "success" | "muted"; icon: readonly string[] } => {
  switch (outcome) {
    case "APPROVE":
      return { tone: "success", icon: ["fas", "check"] };
    case "REFUSE":
      return { tone: "muted", icon: ["fas", "ban"] };
    default: {
      const unexpected: never = outcome;
      return unexpected;
    }
  }
};

export const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);

export const inboxScrollBehavior = (): ScrollBehavior =>
  prefersReducedMotion() ? "auto" : "smooth";
